import React, { useEffect, useState, useCallback } from "react";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { currentWeekStart, addDays, todayMel } from "@/lib/time";
import { FileDown, FileText, Printer, Eye, Mail } from "lucide-react";

export default function AdminReports() {
  const [staff, setStaff] = useState([]);
  const [userId, setUserId] = useState("all");
  const [start, setStart] = useState(currentWeekStart());
  const [end, setEnd] = useState(addDays(currentWeekStart(), 6));
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/admin/staff").then((r) => setStaff(r.data));
  }, []);

  const view = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/report", { params: { user_id: userId, start, end, format: "json" } });
      setReport(data);
    } catch (err) { toast.error(apiError(err)); }
  }, [userId, start, end]);

  const download = async (format) => {
    try {
      const res = await api.get("/admin/report", {
        params: { user_id: userId, start, end, format },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheet_${start}_${end}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const setThisWeek = () => { setStart(currentWeekStart()); setEnd(addDays(currentWeekStart(), 6)); };

  const emailReport = async () => {
    try {
      const { data } = await api.post("/admin/email-report", { user_id: userId, start, end });
      toast.success(data.message);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <h1 className="font-heading text-3xl font-bold no-print">Reports</h1>

      <Card className="p-5 rounded-xl border-border no-print">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div className="space-y-2">
            <Label>Staff member</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="report-staff-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All staff</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
          </div>
          <div className="space-y-2">
            <Label>To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={setThisWeek} data-testid="report-this-week">This week</Button>
            <Button onClick={view} data-testid="report-generate"><Eye className="h-4 w-4 mr-2" /> Generate</Button>
          </div>
        </div>
      </Card>

      {report && (
        <>
          <div className="flex flex-wrap gap-2 no-print">
            <Button variant="outline" onClick={() => window.print()} data-testid="report-print"><Printer className="h-4 w-4 mr-2" /> Print</Button>
            <Button variant="outline" onClick={() => download("csv")} data-testid="report-csv"><FileDown className="h-4 w-4 mr-2" /> CSV</Button>
            <Button variant="outline" onClick={() => download("pdf")} data-testid="report-pdf"><FileText className="h-4 w-4 mr-2" /> PDF</Button>
            <Button variant="outline" onClick={emailReport} data-testid="report-email"><Mail className="h-4 w-4 mr-2" /> Email to me</Button>
          </div>

          <Card className="rounded-xl border-border overflow-hidden" data-testid="report-table">
            <div className="p-5 pb-0 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-semibold">Shady Acres Caravan Park — Timesheet Report</h2>
                <p className="text-sm text-muted-foreground">{report.rows.length} shifts • Total {report.total_display}</p>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Break</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No data for this period.</TableCell></TableRow>}
                {report.rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.staff_name}</TableCell>
                    <TableCell>{r.date_display}</TableCell>
                    <TableCell>{r.clock_in_display || "—"}</TableCell>
                    <TableCell>{r.clock_out_display || "—"}</TableCell>
                    <TableCell>{r.total_break_display}</TableCell>
                    <TableCell className="font-semibold">{r.total_worked_display}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
